import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  ShadcnSelect,
  ShadcnSelectContent,
  ShadcnSelectItem,
  ShadcnSelectTrigger,
  ShadcnSelectValue,
  useAuth,
} from '@aaas/shared';
import pipelineService from '../services/pipeline.service';
import type { PipelineRound, PipelineRoundCreateInput, PipelineRoundUpdateInput } from '../types';
import { ROUND_TYPE_OPTIONS } from '../types';

interface RoundFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  pipelineId: string;
  round?: PipelineRound | null;
  onSuccess: () => void;
}

export function RoundFormDialog({
  open,
  onClose,
  mode,
  pipelineId,
  round,
  onSuccess,
}: RoundFormDialogProps) {
  const { t } = useTranslation('ats');
  const { user, currentWorkspace } = useAuth();
  const [name, setName] = useState('');
  const [roundType, setRoundType] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDefaultRound = useMemo(() => {
    return round?.round_type === 'application_received' || round?.round_type === 'hired';
  }, [round?.round_type]);

  // Filter out default round types for create mode
  const availableRoundTypes = useMemo(() => {
    return !isDefaultRound
      ? ROUND_TYPE_OPTIONS.filter(
          (option) => option.value !== 'application_received' && option.value !== 'hired'
        )
      : ROUND_TYPE_OPTIONS;
  }, [isDefaultRound]);

  // Reset form when dialog opens or round changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && round) {
        setName(round.name);
        setRoundType(round.round_type);
      } else {
        setName('');
        setRoundType('');
      }
      setError(null);
    }
  }, [open, mode, round]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentWorkspace) return;
    if (!name.trim()) {
      setError(t('common.validation.nameRequired'));
      return;
    }
    if (!roundType) {
      setError(t('pipeline.roundDialog.errors.typeRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await user.getIdToken();

      if (mode === 'create') {
        const data: PipelineRoundCreateInput = {
          name: name.trim(),
          round_type: roundType,
        };
        await pipelineService.createRound(pipelineId, currentWorkspace.id, token, data);
      } else if (round) {
        const data: PipelineRoundUpdateInput = {
          name: name.trim(),
          // Only allow changing round_type for non-default rounds
          ...(isDefaultRound ? {} : { round_type: roundType }),
        };
        await pipelineService.updateRound(pipelineId, round.id, currentWorkspace.id, token, data);
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('config.unknownError');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? t('pipeline.roundDialog.createTitle') : t('pipeline.roundDialog.editTitle')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('pipeline.roundDialog.nameLabel')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('pipeline.roundDialog.namePlaceholder')}
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="round_type">{t('pipeline.roundDialog.typeLabel')}</Label>
            <ShadcnSelect
              value={roundType}
              onValueChange={setRoundType}
              disabled={isDefaultRound || isSubmitting}
            >
              <ShadcnSelectTrigger className="w-full !h-10 rounded-md border-gray-300 focus-visible:ring-2 focus-visible:ring-blue-500">
                <ShadcnSelectValue placeholder={t('pipeline.roundDialog.typePlaceholder')} />
              </ShadcnSelectTrigger>
              <ShadcnSelectContent position="popper" sideOffset={4} className="max-h-60 z-50 min-w-[var(--radix-select-trigger-width)] rounded-md border bg-popover shadow-md">
                {availableRoundTypes.map((option) => (
                  <ShadcnSelectItem
                    key={option.value}
                    value={option.value}
                    className="h-8 cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    {option.label}
                  </ShadcnSelectItem>
                ))}
              </ShadcnSelectContent>
            </ShadcnSelect>
            {isDefaultRound && mode === 'edit' && (
              <p className="text-xs text-gray-500">
                {t('pipeline.roundDialog.defaultTypeLockedHint')}
              </p>
            )}
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {t('config.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {mode === 'create' ? t('pipeline.roundDialog.adding') : t('common.saving')}
                </>
              ) : mode === 'create' ? (
                t('pipeline.roundDialog.addButton')
              ) : (
                t('common.saveChanges')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
